'use client';

import { cn } from '@bridebook/aura';
import { ModalControlContext, ModalControlContextType } from '../modal-control-context';
import { IsScrolledContextProvider } from '../is-scrolled-context';
import { RoundedModalOverlay } from './rounded-modal-overlay';
import { RoundedModalContainerContext } from './rounded-modal-container-context';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ComponentPropsWithoutRef, ElementRef, forwardRef, useMemo } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

const roundedModalContainerVariants = cva('', {
  variants: {
    width: {
      // default width for simple info modals - it is responsive for mobile and desktop. sm or standard
      'responsive-sm': 'w-full max-w-[min(calc(100%-16px),343px)] lg:max-w-[600px]',
      // Fixed width: sm
      sm: 'w-full max-w-[min(calc(100%-16px),343px)]',
      // Fixed width: standard. Used for large content like forms, scrollable elements, multiple accordions, etc.
      standard: 'w-full max-w-[min(calc(100%-16px),600px)]',
      // Fixed width: lg. Used for large content like forms, scrollable elements, multiple accordions, etc.
      lg: 'w-full max-w-[min(calc(100%-16px),800px)]',
    },
    height: {
      // height of the modal adjusts to the content up to 90vh
      'auto-height': 'max-h-[90vh]',
      // height of the modal is fixed to the screen size minus 200px (usefull for multi steps forms inside a modal, when you don't want the height to change between the steps)
      'fixed-height': 'h-[calc(100dvh-200px)]',
    },
  },
});

export type RoundedModalContainerWidth = NonNullable<
  VariantProps<typeof roundedModalContainerVariants>['width']
>;
export type RoundedModalContainerHeight = NonNullable<
  VariantProps<typeof roundedModalContainerVariants>['height']
>;

export type RoundedModalProps = Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
  'open' | 'defaultOpen'
> & {
  isOpen: boolean;
  width?: RoundedModalContainerWidth;
  height?: RoundedModalContainerHeight;
  className?: string;
  /** Function to force close the modal bypassing onOpenChange logic.
   * Usefull for example when you need to close the modal at the end of some flow (like long form) and bypass the regular onOpenChange logic where you might have some analytics of "are you sure you want to close?" etc. */
  forceClose?: () => void;
  classNames?: {
    overlay?: string;
  };
} & Pick<
    ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    'onOpenAutoFocus' | 'onInteractOutside'
  >;

export const RoundedModal = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  RoundedModalProps
>(
  (
    {
      children,
      isOpen,
      onOpenChange,
      forceClose,
      width = 'responsive-sm',
      height = 'auto-height',
      className,
      classNames,
      ...props
    },
    ref,
  ) => {
    const controlContextValue = useMemo(
      () =>
        ({
          isOpen,
          onOpenChange,
          close: () => onOpenChange?.(false),
          forceClose,
        }) satisfies ModalControlContextType,
      [isOpen, onOpenChange, forceClose],
    );
    return (
      <ModalControlContext.Provider value={controlContextValue}>
        <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange} {...props}>
          <RoundedModalContainerContext.Provider value={{ width, height }}>
            <IsScrolledContextProvider>
              <RoundedModalPortal>
                <RoundedModalOverlay className={cn('z-[10000]', classNames?.overlay)} />
                <DialogPrimitive.Content
                  aria-describedby={undefined}
                  ref={ref}
                  data-slot="rounded-modal-content"
                  className={cn(
                    roundedModalContainerVariants({ width, height }),
                    'fixed top-[50%] left-[50%] z-[10000] w-full translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-white shadow-bb-shadow',
                    'flex flex-col',
                    // animation
                    'duration-200',
                    // open animation (matching old modals)
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-[50px]',
                    // close animation. Not using the close animation because it adds a lot of complexity when cleaning up the modal, which with close animation should be delayed.
                    //'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    className,
                  )}
                  {...props}>
                  {children}
                </DialogPrimitive.Content>
              </RoundedModalPortal>
            </IsScrolledContextProvider>
          </RoundedModalContainerContext.Provider>
        </DialogPrimitive.Root>
      </ModalControlContext.Provider>
    );
  },
);
RoundedModal.displayName = 'RoundedModal';

export const RoundedModalPortal = DialogPrimitive.Portal;
